import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import authService from '../services/authService';

// Configuration de l'API pour ce hook - utilise l'URL relative en production
const api = axios.create({
  baseURL: '/api', // URL relative pour éviter les problèmes CORS
  timeout: 60000,
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  config => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  response => response,
  error => {
    // Si l'erreur est 401 (non autorisé), déconnecter l'utilisateur
    if (error.response?.status === 401) {
      authService.logout();
      // Rediriger vers la page de connexion
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

interface ProgressData {
  taskId: string;
  phase: 'starting' | 'processing' | 'sorting' | 'completed' | 'failed';
  current: number;
  total: number;
  percentage: number;
  message: string;
  batch?: number;
  totalBatches?: number;
  estimatedTimeLeft?: number;
  error?: string;
  startTime?: number;
  endTime?: number;
  duration?: number;
}

interface UseProgressReturn {
  progress: ProgressData | null;
  isLoading: boolean;
  error: string | null;
  startProgress: () => Promise<string | null>;
  stopPolling: () => void;
  resetProgress: () => void;
}

export const useProgress = (
  startEndpoint: string,
  pollInterval: number = 1000
): UseProgressReturn => {
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [pollTimer, setPollTimer] = useState<NodeJS.Timeout | null>(null);

  // Fonction pour arrêter le polling
  const stopPolling = useCallback(() => {
    if (pollTimer) {
      clearInterval(pollTimer);
      setPollTimer(null);
    }
  }, [pollTimer]);

  // Fonction pour récupérer la progression
  const fetchProgress = useCallback(async (currentTaskId: string) => {
    try {
      const response = await api.get(`/progress/${currentTaskId}`);
      const progressData = response.data;
      
      setProgress(progressData);
      
      // Arrêter le polling si la tâche est terminée ou a échoué
      if (progressData.phase === 'completed' || progressData.phase === 'failed') {
        stopPolling();
      }
      
      return progressData;
    } catch (err: any) {
      console.error('Erreur lors de la récupération de la progression:', err);
      
      // Si la tâche n'existe pas (404), arrêter le polling
      if (err.response?.status === 404) {
        setError('Tâche de progression introuvable');
        stopPolling();
      } else {
        setError(`Erreur de progression: ${err.response?.data?.message || err.message}`);
      }
      
      return null;
    }
  }, [stopPolling]);

  // Fonction pour démarrer une tâche avec progression
  const startProgress = useCallback(async (): Promise<string | null> => {
    setIsLoading(true);
    setError(null);
    setProgress(null);
    
    try {
      console.log(`Démarrage de la tâche: ${startEndpoint}`);
      const response = await api.post(startEndpoint);
      const { taskId: newTaskId } = response.data;
      
      setTaskId(newTaskId);
      console.log(`Tâche démarrée avec ID: ${newTaskId}`);
      
      // Commencer le polling
      const timer = setInterval(async () => {
        await fetchProgress(newTaskId);
      }, pollInterval);
      
      setPollTimer(timer);
      setIsLoading(false);
      
      return newTaskId;
    } catch (err: any) {
      console.error('Erreur lors du démarrage de la tâche:', err);
      setError(`Erreur de démarrage: ${err.response?.data?.message || err.message}`);
      setIsLoading(false);
      return null;
    }
  }, [startEndpoint, pollInterval, fetchProgress]);

  // Fonction pour réinitialiser l'état
  const resetProgress = useCallback(() => {
    stopPolling();
    setProgress(null);
    setError(null);
    setTaskId(null);
    setIsLoading(false);
  }, [stopPolling]);

  // Nettoyage lors du démontage du composant
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    progress,
    isLoading,
    error,
    startProgress,
    stopPolling,
    resetProgress
  };
};

export default useProgress;
